import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { ApiError } from '../../services/online/apiClient';
import OnlineRondaScreen from './OnlineRondaScreen';
import OnlineBacigaScreen from './OnlineBacigaScreen';
import CutTheDeckModal from './CutTheDeckModal';
import { getAiPersona } from '../../game/online/aiPersonas';

/**
 * Dispatches /online/play/:roomId to the right game screen by the room's
 * gameType, AND now owns the match-level orchestration on top of that
 * (a room plays a whole MATCH — several rounds, matchTransition.ts —
 * instead of stopping after one): shows the one-time cut-the-deck ceremony
 * at the true start of a fresh match, and re-mounts the (possibly
 * different, under CLASSIC mode) game screen every time a round transitions,
 * via the `onRoundTransition` callback each child screen calls once its own
 * socket closes after a ROUND_TRANSITION. Fetches the room once (via
 * /rooms/mine) purely to read this; each destination screen still does its
 * own independent room fetch afterward, exactly as it always has.
 */
export default function OnlinePlayScreen({ spectator = false }) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { roomId } = useParams();
    const { authFetch } = useAuth();
    const [gameType, setGameType] = useState(null);
    const [roundKey, setRoundKey] = useState(0);
    const [cutCeremony, setCutCeremony] = useState(null);

    useEffect(() => {
        let cancelled = false;

        // A spectator joins a match already under way: no cut-the-deck
        // ceremony to show, and /rooms/mine has nothing for them — only the
        // game type is needed to pick the right screen.
        if (spectator) {
            authFetch(`/rooms/live/${roomId}`)
                .then((room) => {
                    if (!cancelled) setGameType(room.gameType || 'RONDA');
                })
                .catch(() => {
                    if (!cancelled) navigate('/online', { replace: true });
                });
            return () => {
                cancelled = true;
            };
        }

        authFetch('/rooms/mine')
            .then((room) => {
                if (cancelled) return;
                if (room.id !== roomId) {
                    navigate('/online', { replace: true });
                    return;
                }
                setGameType(room.gameType || 'RONDA');

                // Shown exactly once per match, the first time this browser
                // tab loads a fresh one (matchScore 0-0) — sessionStorage
                // survives this component re-mounting on a round transition
                // but not a real page reload mid-round, which is fine: the
                // ceremony's own result (already persisted) never changes.
                const isFreshMatch = !room.matchScoreTeam1 && !room.matchScoreTeam2;
                const storageKey = `online_cut_ceremony_shown:${room.id}`;
                if (isFreshMatch && room.cutCuttingSeat != null && !sessionStorage.getItem(storageKey)) {
                    sessionStorage.setItem(storageKey, '1');
                    const playersBySeat = [0, 1, 2, 3].map((seat) => {
                        const seatInfo = room.seats.find((s) => s.seat === seat);
                        if (seatInfo?.occupantType === 'HUMAN') return { name: seatInfo.userName || '?', isSystem: false };
                        if (seatInfo?.occupantType === 'SYSTEM') {
                            const persona = getAiPersona(room.id, seat);
                            return { name: `${persona.name} ${t('online.play.ai_name_suffix')}`, avatar: persona.avatar, isSystem: true };
                        }
                        return { name: '', isSystem: false };
                    });
                    setCutCeremony({
                        cuttingSeat: room.cutCuttingSeat,
                        cardSuit: room.cutCardSuit,
                        cardValue: room.cutCardValue,
                        dealerSeat: room.nextDealerSeat,
                        playersBySeat,
                    });
                }
            })
            .catch((err) => {
                if (cancelled) return;
                if (err instanceof ApiError && err.code === 'ROOM_NOT_FOUND') {
                    navigate('/online', { replace: true });
                } else {
                    // Default to Ronda's screen so its own fetch can surface the real error the same way it always has.
                    setGameType('RONDA');
                }
            });
        return () => {
            cancelled = true;
        };
    }, [authFetch, navigate, roomId, t, spectator]);

    const handleRoundTransition = useCallback((nextGameType) => {
        setGameType(nextGameType);
        setRoundKey((k) => k + 1);
    }, []);

    if (!gameType) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (cutCeremony) {
        return <CutTheDeckModal {...cutCeremony} onContinue={() => setCutCeremony(null)} />;
    }

    // Keyed by round so a transition (possibly to a different game type
    // under CLASSIC mode) always gets a genuinely fresh mount — a fresh
    // Pixi app and WS connection, no stale refs left over from the
    // just-finished round.
    const key = `${gameType}-${roundKey}`;
    return gameType === 'BACIGA' ? (
        <OnlineBacigaScreen key={key} onRoundTransition={handleRoundTransition} spectator={spectator} />
    ) : (
        <OnlineRondaScreen key={key} onRoundTransition={handleRoundTransition} spectator={spectator} />
    );
}
